/**
 * Hooks React pour le catalogue d'exercices.
 * Pattern useState + useEffect, coherent avec le reste du projet
 * (pas de React Query, pas de SWR).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createProcede as apiCreate,
  deleteProcede as apiDelete,
  duplicateProcede as apiDuplicate,
  getErrorMessage,
  getProcede as apiGet,
  listProcedes,
  listProcedeTemplates,
  updateProcede as apiUpdate,
} from '../services/procedes'

/**
 * Liste d'exercices paginee avec filtres reactifs.
 *
 * @param {object} filters - {procede, category_tactique, search, is_template, page, size, mode}
 *                           mode='templates' utilise GET /exercises/templates/
 *                           sinon GET /exercises/ (qui inclut deja templates + perso + club)
 */
export function useProcedeList(filters) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const reqIdRef = useRef(0)

  const fetchList = useCallback(async () => {
    const reqId = ++reqIdRef.current
    setLoading(true)
    setError(null)
    try {
      const fn = filters?.mode === 'templates' ? listProcedeTemplates : listProcedes
      const result = await fn(filters)
      // Ignore les reponses obsoletes (filtres ont change pendant le fetch)
      if (reqId !== reqIdRef.current) return
      setData(result)
    } catch (err) {
      if (reqId !== reqIdRef.current) return
      setError(getErrorMessage(err, "Impossible de charger les exercices."))
    } finally {
      if (reqId === reqIdRef.current) setLoading(false)
    }
  }, [
    filters?.mode,
    filters?.procede,
    filters?.category_tactique,
    filters?.search,
    filters?.is_template,
    filters?.theme,
    filters?.page,
    filters?.size,
  ])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  return { data, loading, error, refetch: fetchList }
}

/**
 * Detail d'un exercice par id.
 */
export function useProcede(id) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchOne = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const result = await apiGet(id)
      setData(result)
    } catch (err) {
      setError(getErrorMessage(err, "Impossible de charger l'exercice."))
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchOne()
  }, [fetchOne])

  return { data, loading, error, refetch: fetchOne }
}

/**
 * Mutation create. Retourne {mutate(payload), loading, error, fieldErrors}.
 * fieldErrors : objet par champ pour afficher les erreurs de validation
 * cote formulaire (parsing 422 Pydantic).
 */
export function useCreateProcede() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})

  const mutate = useCallback(async (payload) => {
    setLoading(true)
    setError(null)
    setFieldErrors({})
    try {
      const result = await apiCreate(payload)
      return result
    } catch (err) {
      const fe = parseValidationErrors(err)
      setFieldErrors(fe)
      setError(getErrorMessage(err, "Création impossible."))
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return { mutate, loading, error, fieldErrors }
}

export function useUpdateProcede() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})

  const mutate = useCallback(async (id, payload) => {
    setLoading(true)
    setError(null)
    setFieldErrors({})
    try {
      const result = await apiUpdate(id, payload)
      return result
    } catch (err) {
      const fe = parseValidationErrors(err)
      setFieldErrors(fe)
      setError(getErrorMessage(err, "Modification impossible."))
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return { mutate, loading, error, fieldErrors }
}

export function useDeleteProcede() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const mutate = useCallback(async (id) => {
    setLoading(true)
    setError(null)
    try {
      await apiDelete(id)
    } catch (err) {
      setError(getErrorMessage(err, "Suppression impossible."))
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return { mutate, loading, error }
}

export function useDuplicateProcede() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const mutate = useCallback(async (id) => {
    setLoading(true)
    setError(null)
    try {
      return await apiDuplicate(id)
    } catch (err) {
      setError(getErrorMessage(err, "Duplication impossible."))
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return { mutate, loading, error }
}

/**
 * Parse les erreurs Pydantic 422 pour les afficher par champ.
 * Format Pydantic v2 : { detail: [{loc:["body","name"], msg, type}, ...] }
 */
function parseValidationErrors(err) {
  const detail = err?.response?.data?.detail
  if (!Array.isArray(detail)) return {}
  const fe = {}
  for (const item of detail) {
    const loc = item?.loc
    if (Array.isArray(loc) && loc.length >= 2) {
      const field = loc[loc.length - 1]
      if (!fe[field]) fe[field] = item.msg || 'Valeur invalide'
    }
  }
  return fe
}
